import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import React, { memo, useCallback, useMemo } from 'react';
import {
  AreaData,
  DateData,
  DurationData,
  LocationChangeParameters,
  LocationData,
  PersonsData,
  RoomsState,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import SearchBarBrandDropdown from '@/components/organisms/search-bar/shared/search-bar-brand/search-bar-brand-dropdown/search-bar-brand-dropdown';
import SearchBarDateDropdown from '@/components/organisms/search-bar/shared/search-bar-date-dropdown/search-bar-date-dropdown';
import SearchBarLocationsDropdown from '@/components/organisms/search-bar/shared/search-bar-locations-dropdown/search-bar-locations-dropdown';
import SearchBarPersonsDropdown from '@/components/organisms/search-bar/shared/search-bar-persons-dropdown/search-bar-persons-dropdown';
import SearchBarWidgetDropdown from '../search-bar-widget-dropdown/search-bar-widget-dropdown';
import styles from './search-bar-dropdown.module.scss';

export interface SearchBarSectionData {
  disabled?: boolean;
  dropdownData?:
    | AreaData
    | DurationData
    | LocationData
    | DateData
    | PersonsData;
  id: number;
  isActive?: boolean;
  isNotified?: boolean;
  label: string;
  placeholder?: string;
  type: SearchBarWrapperSectionType;
  value?: string[] | string;
}

export interface SearchBarDropdownProps {
  /** The active section id */
  activeSection?: number;
  /** The class name to apply */
  className?: string;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** Indicates whether the date section is loading */
  isLoadingDate?: boolean;
  /** Indicates whether the locations section is loading */
  isLoadingLocations?: boolean;
  /** Indicates whether the search bar is used in the search widget */
  isWidget?: boolean;
  /** An event that is triggered on brand change */
  onChangeBrand?: (brand: Content.Properties.Brand) => void;
  /** An event that is triggered on date change */
  onChangeDate?: (dates: Date[]) => void;
  /** An event that is triggered on duration change */
  onChangeDuration?: (id: string) => void;
  /** An event that is triggered on location change */
  onChangeLocations?: ({
    destinationRegions,
    destinations,
    hotels,
    locations,
  }: LocationChangeParameters) => void;
  /** An event that is triggered on persons change */
  onChangePersons?: (roomsState: RoomsState) => void;
  /** An event that is triggered on transportation change */
  onChangeTransportation?: (id: string) => void;
  /** An event that is triggered on click back */
  onClickBack?: () => void;
  /** An event that is triggered on close click */
  onClickClose?: () => void;
  /** An event that is triggered on next click */
  onClickNext?: () => void;
  /** An event that is triggered on search click */
  onClickSearch?: () => void;
  /** The search settings */
  searchSettings?: Content.Components['SearchSettingsDto'];
  /** Sections to render in the search bar */
  sections?: SearchBarSectionData[];
}

const SearchBarDropdown = memo(
  ({
    activeSection = -1,
    className,
    customRangeStyle = false,
    isLoadingDate = false,
    isLoadingLocations = false,
    isWidget = false,
    onChangeBrand,
    onChangeDate,
    onChangeDuration,
    onChangeLocations,
    onChangePersons,
    onChangeTransportation,
    onClickBack,
    onClickClose,
    onClickNext,
    onClickSearch,
    searchSettings,
    sections = [],
    ...rest
  }: SearchBarDropdownProps) => {
    const hasBrandSelectorSection = useMemo(
      () =>
        sections?.some(
          section => section.type === SearchBarWrapperSectionType.BRAND,
        ),
      [sections],
    );

    const { locationSection, dateSection, widgetSection } = useMemo(() => {
      if (!sections || sections.length === 0) {
        return {
          dateSection: undefined as any,
          locationSection: undefined as any,
          widgetSection: undefined,
        };
      }

      try {
        const location = sections.find(
          section => section.type === SearchBarWrapperSectionType.LOCATIONS,
        ) as SearchBarSectionData;
        const date = sections.find(
          section => section.type === SearchBarWrapperSectionType.DATE,
        ) as SearchBarSectionData;
        const widget = sections.find(section => section.id === activeSection);

        return {
          dateSection: date,
          locationSection: location,
          widgetSection: widget,
        };
      } catch {
        return {
          dateSection: undefined as any,
          locationSection: undefined as any,
          widgetSection: undefined,
        };
      }
    }, [sections, activeSection]);

    const activeSectionType = widgetSection?.type;

    const renderSearchBarSectionDropdown = useCallback(
      (section: SearchBarSectionData) => {
        switch (section.type) {
          case SearchBarWrapperSectionType.BRAND:
            return (
              <SearchBarBrandDropdown
                className={classNames(
                  styles.sectionDropdown,
                  styles.isBrandDropdown,
                )}
                isVisible={section.id === activeSection}
                label={section.label}
                onChange={onChangeBrand}
                onClickClose={onClickClose}
                onClickNext={onClickNext}
              />
            );

          case SearchBarWrapperSectionType.PERSONS:
            return (
              <SearchBarPersonsDropdown
                adultThreshold={
                  (section.dropdownData as PersonsData)?.adultThreshold ?? 0
                }
                className={classNames(
                  styles.sectionDropdown,
                  styles.isPersonsDropdown,
                )}
                defaultAdultNumber={
                  (section.dropdownData as PersonsData)?.defaultAdultNumber
                }
                description={section.value ?? section.placeholder}
                isVisible={section.id === activeSection}
                label={section.label}
                maxPersons={(section.dropdownData as PersonsData)?.maxPersons}
                maxRooms={(section.dropdownData as PersonsData)?.maxRooms}
                onChange={onChangePersons}
                onClickBack={onClickBack}
                onClickClose={onClickClose}
                onClickNext={onClickNext}
                roomDistributionWarning={
                  (section.dropdownData as PersonsData)?.roomDistributionWarning
                }
                roomsState={(section.dropdownData as PersonsData)?.roomsState}
              />
            );

          default:
            return null;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [activeSection],
    );

    return (
      <div
        {...rest}
        className={classNames(styles.component, className, {
          [styles.isSearchWidget]: isWidget,
        })}
      >
        {!isWidget ? (
          <React.Fragment>
            <div
              className={classNames(styles.searchBarWrapper, {
                [styles.hasThreeSections]: sections.length === 3,
                [styles.hasFourSections]: sections.length === 4,
                [styles.hasBrandSelector]: hasBrandSelectorSection,
              })}
            >
              {sections.map(section => (
                <div key={section.id} className={styles.searchSectionWrapper}>
                  <AnimatePresence initial={false} mode="wait">
                    {activeSectionType && section.type === activeSectionType
                      ? renderSearchBarSectionDropdown(section)
                      : null}
                  </AnimatePresence>
                </div>
              ))}

              <div className={styles.button} />
            </div>

            <div className={styles.underBarWrapper}>
              <AnimatePresence initial={false} mode="wait">
                {locationSection.type === activeSectionType && (
                  <SearchBarLocationsDropdown
                    key={`locations-${locationSection.id}`}
                    className={styles.isLocationsDropdown}
                    description={
                      Array.isArray(locationSection.value) &&
                      locationSection.value.length > 0
                        ? locationSection.value
                        : locationSection.placeholder
                    }
                    isLoading={isLoadingLocations}
                    isVisible
                    label={locationSection.label}
                    locations={
                      (locationSection.dropdownData as LocationData)
                        ?.locations ?? []
                    }
                    onChangeLocations={onChangeLocations}
                    onChangeTransportation={onChangeTransportation}
                    onClickBack={onClickBack}
                    onClickClose={onClickClose}
                    onClickNext={onClickNext}
                    searchSettings={searchSettings}
                    transportationOptions={
                      (locationSection.dropdownData as LocationData)
                        ?.transportationOptions ?? []
                    }
                  />
                )}

                {dateSection.type === activeSectionType && (
                  <SearchBarDateDropdown
                    key={`date-${dateSection.id}`}
                    availableDates={
                      (dateSection.dropdownData as DateData)?.availableDates ??
                      []
                    }
                    charterDates={
                      (dateSection.dropdownData as DateData)?.charterDates
                    }
                    className={classNames(styles.isDateDropdown)}
                    customRangeStyle={customRangeStyle}
                    description={
                      dateSection.value && dateSection.value !== ''
                        ? dateSection.value
                        : dateSection.placeholder
                    }
                    durationOptions={
                      (dateSection.dropdownData as DateData)?.durationOptions ??
                      []
                    }
                    focusFirstSelectableMonth={
                      (dateSection.dropdownData as DateData)
                        ?.focusFirstSelectableMonth
                    }
                    isLoading={isLoadingDate}
                    isVisible
                    label={dateSection.label}
                    maxDate={
                      (dateSection.dropdownData as DateData)?.maxDate as Date
                    }
                    maxRangeDays={
                      (dateSection.dropdownData as DateData)?.maxRangeDays
                    }
                    minDate={
                      (dateSection.dropdownData as DateData)?.minDate as Date
                    }
                    onChange={onChangeDate}
                    onChangeDuration={onChangeDuration}
                    onClickBack={onClickBack}
                    onClickClose={onClickClose}
                    onClickNext={onClickNext}
                    onClickSearch={onClickSearch}
                    rangeSelect={
                      (dateSection.dropdownData as DateData)?.rangeSelect
                    }
                    rangeSize={
                      (dateSection.dropdownData as DateData)?.rangeSize
                    }
                    returnDurations={
                      (dateSection.dropdownData as DateData)?.returnDurations
                    }
                    selectedDate={
                      (dateSection.dropdownData as DateData)?.selectedDate
                    }
                    selectedMaxDate={
                      (dateSection.dropdownData as DateData)?.selectedMaxDate
                    }
                  />
                )}
              </AnimatePresence>
            </div>
          </React.Fragment>
        ) : (
          <SearchBarWidgetDropdown
            customRangeStyle={customRangeStyle}
            onChangeDate={onChangeDate}
            onChangeDuration={onChangeDuration}
            onChangeLocations={onChangeLocations}
            onChangePersons={onChangePersons}
            onChangeTransportation={onChangeTransportation}
            onClickBack={onClickBack}
            onClickClose={onClickClose}
            onClickNext={onClickNext}
            onClickSearch={onClickSearch}
            section={widgetSection}
          />
        )}
      </div>
    );
  },
);

SearchBarDropdown.displayName = 'SearchBarDropdown';

export default SearchBarDropdown;
